import { animated, useSpring } from '@react-spring/web'
import { ChevronDown, LayoutDashboard, Power, Settings } from 'lucide-react'
import React, { useState } from 'react'
import { Link, useLocation, useSubmit } from 'react-router'
import { Avatar, AvatarFallback, AvatarImage } from '#app/components/ui/avatar'
import { content } from '#app/data/content'
import { bouncySpring, prefersReducedMotion } from '#app/utils/animations'
import { createAvatarName } from '#app/utils/avatar-name'
import { cn } from '#app/utils/misc'
import { useOptionalUser } from '#app/utils/user'
import { Logo } from './nav-logo'
import { AnimatedButton } from './ui/animated-button'
import { Button } from './ui/button'
import { Icon } from './ui/icon'

export interface NavItemType {
	readonly name: string
	readonly href?: string
	readonly muted?: boolean
	readonly children?: readonly NavItemType[]
}
interface NavItemProps {
	item: NavItemType
	openDropdown: string | null
	setOpenDropdown: React.Dispatch<React.SetStateAction<string | null>>
}

const marketingNavItems: readonly NavItemType[] =
	content.shared.navigation.marketing

const landlordNavItems: readonly NavItemType[] =
	content.shared.navigation.landlord

export function NavBar({
	isLoggedIn = false,
	routeType: _routeType,
	setSidebarOpen: _setSidebarOpen,
	theme: _theme,
	hideToggle: hideToggleProp = false,
	viewerName,
}: {
	isLoggedIn?: boolean
	routeType: string
	setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>
	theme: null | string
	/** Force-hide the Tenant/Landlord toggle (e.g. when reused inside the screening flow). */
	hideToggle?: boolean
	/** Public demo has no auth session — pass the persona's name so the user
	 * dropdown shows it instead of an empty avatar. Takes precedence over any
	 * resolved user, so the demo always reads as that persona. */
	viewerName?: string
}) {
	const [hasProgressBar, setHasProgressBar] = React.useState(false)
	// Frosted variant: the screening consent modal blurs this shared header to
	// match its backdrop by setting `data-navbar-blur` on <body>.
	const [blurHeader, setBlurHeader] = React.useState(false)

	/* ---------------- Observe screening progress bar ---------------- */
	React.useEffect(() => {
		const check = () => {
			setHasProgressBar(
				document.body.hasAttribute('data-has-screening-progress'),
			)
			setBlurHeader(document.body.hasAttribute('data-navbar-blur'))
		}

		check()

		const observer = new MutationObserver(check)
		observer.observe(document.body, {
			attributes: true,
			attributeFilter: ['data-has-screening-progress', 'data-navbar-blur'],
		})

		return () => observer.disconnect()
	}, [])

	// Hidden when the screening progress bar is present, or forced off via prop.
	const hideToggle = hideToggleProp || hasProgressBar

	const submit = useSubmit()
	const user = useOptionalUser()
	const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false)
	// const [openDropdown, setOpenDropdown] = React.useState<string | null>(null)
	const [desktopDropdown, setDesktopDropdown] = React.useState<string | null>(
		null,
	)
	const [mobileDropdown, setMobileDropdown] = React.useState<string | null>(
		null,
	)

	const [navbarHeight, setNavbarHeight] = React.useState(0)
	const [isNavbarVisible, setIsNavbarVisible] = React.useState(true)
	const navbarRef = React.useRef<HTMLDivElement>(null)
	const lastScrollY = React.useRef(0)
	const location = useLocation()

	React.useLayoutEffect(() => {
		const updateNavbarHeight = () => {
			if (navbarRef.current) {
				const height = navbarRef.current.offsetHeight
				setNavbarHeight(height)
				// Set CSS custom property for navbar height
				document.documentElement.style.setProperty(
					'--navbar-height',
					`${height}px`,
				)
			}
		}
		updateNavbarHeight()
		window.addEventListener('resize', updateNavbarHeight)
		return () => window.removeEventListener('resize', updateNavbarHeight)
	})

	// Initialize scroll position
	React.useEffect(() => {
		lastScrollY.current = window.scrollY
	}, [])

	// Hide/show navbar on scroll
	React.useEffect(() => {
		// Always show navbar when mobile menu is open
		if (mobileMenuOpen) {
			setIsNavbarVisible(true)
			return
		}

		let ticking = false

		const handleScroll = () => {
			if (!ticking) {
				window.requestAnimationFrame(() => {
					const currentScrollY = window.scrollY
					const scrollDifference = currentScrollY - lastScrollY.current

					// Always show navbar at the top of the page
					if (currentScrollY < 10) {
						setIsNavbarVisible(true)
					} else if (scrollDifference > 5) {
						// Scrolling down - hide navbar
						setIsNavbarVisible(false)
					} else if (scrollDifference < -5) {
						// Scrolling up - show navbar
						setIsNavbarVisible(true)
					}

					lastScrollY.current = currentScrollY
					ticking = false
				})
				ticking = true
			}
		}

		window.addEventListener('scroll', handleScroll, { passive: true })

		return () => {
			window.removeEventListener('scroll', handleScroll)
		}
	}, [mobileMenuOpen])

	// Close dropdown when scrolling
	React.useEffect(() => {
		const handleScroll = () => {
			if (desktopDropdown) {
				setDesktopDropdown(null)
			}
		}
		window.addEventListener('scroll', handleScroll, { passive: true })
		return () => window.removeEventListener('scroll', handleScroll)
	}, [desktopDropdown])

	// Prevent body scroll when mobile menu is open
	React.useEffect(() => {
		if (mobileMenuOpen) {
			// Save current scroll position
			const scrollY = window.scrollY
			document.body.style.overflow = 'hidden'
			document.body.style.position = 'fixed'
			document.body.style.top = `-${scrollY}px`
			document.body.style.width = '100%'
		} else {
			// Restore scroll position
			const scrollY = document.body.style.top
			document.body.style.overflow = ''
			document.body.style.position = ''
			document.body.style.top = ''
			document.body.style.width = ''
			if (scrollY) {
				window.scrollTo(0, Number.parseInt(scrollY || '0', 10) * -1)
			}
		}
		return () => {
			// Cleanup on unmount - ensure scroll is always restored
			document.body.style.overflow = ''
			document.body.style.position = ''
			document.body.style.top = ''
			document.body.style.width = ''
		}
	}, [mobileMenuOpen])
	// Check if current route is any landlord-related route
	const landlordRoutes = content.shared.navigation.landlordRoute
	const isLandlordMode = landlordRoutes.some((route) =>
		location.pathname.startsWith(route),
	)
	const currentNavItems = isLandlordMode ? landlordNavItems : marketingNavItems

	React.useEffect(() => {
		if (mobileMenuOpen) {
			setDesktopDropdown(null)
		}
	}, [mobileMenuOpen])

	return (
		<>
			<div
				ref={navbarRef}
				className={cn(
					'fixed top-0 right-0 left-0 z-50 w-full bg-[#FBFDFD] shadow-[0px_4px_4px_0px_#0000000D] transition-transform duration-300 ease-in-out',
					blurHeader &&
						'top-0 border-b border-[#32302F]/10 bg-[#979696] shadow-none blur-[3px]',
				)}
				style={{
					transform: isNavbarVisible ? 'translateY(0)' : 'translateY(-100%)',
				}}
			>
				<div className="relative w-full">
					<div
						className={`mx-auto px-4 pb-4 md:px-[100px] ${
							hideToggle ? 'pt-4' : ''
						}`}
					>
						<div className="flex flex-col gap-3">
							{/* Top Row - Toggle Buttons */}
							{!hideToggle && (
								<div className="flex justify-start min-[1300px]:flex min-[1300px]:justify-start">
									<ToggleButtons isLandlordMode={isLandlordMode} />
								</div>
							)}

							{/* Bottom Row - Main Navigation */}
							<div className="relative flex w-full items-center">
								{/* LEFT — Logo */}
								<div className="flex-shrink-0">
									<LogoButton isLoggedIn={isLoggedIn} />
								</div>

								{/* CENTER — Desktop Nav Items */}
								<div className="absolute left-1/2 hidden -translate-x-1/2 items-end space-x-8 font-medium whitespace-nowrap min-[1300px]:flex">
									{currentNavItems.map((item) => (
										<NavItem
											key={item.name}
											item={item}
											openDropdown={desktopDropdown}
											setOpenDropdown={setDesktopDropdown}
										/>
									))}
								</div>

								{/* RIGHT — Desktop Auth */}
								<div className="ml-auto hidden items-center space-x-4 min-[1300px]:flex">
									{!isLoggedIn && (
										<Link
											to="/blog"
											className="text-sm font-medium text-[#32302F] transition-colors hover:text-black"
										>
											Blog
										</Link>
									)}

									{!isLoggedIn && !viewerName ? (
										<>
											<LoginButton isLandlordMode={isLandlordMode} />
											<SignupButton isLandlordMode={isLandlordMode} />
										</>
									) : (
										<UserDropdown
											submit={submit}
											user={user}
											blurHeader={blurHeader}
											viewerName={viewerName}
										/>
									)}
								</div>

								{/* ✅ RIGHT — Mobile Hamburger */}
								<div className="ml-auto flex items-center max-[1299px]:flex min-[1300px]:hidden">
									<Button
										variant="ghost"
										size="icon"
										onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
										className="text-slate-700 hover:text-slate-900"
									>
										{mobileMenuOpen ? (
											<Icon name="cross-1" className="h-6 w-6" />
										) : (
											<img
												src="/img/landlords/hamburger-icon.png"
												className="h-6 w-6"
												alt="Menu"
											/>
										)}
										<span className="sr-only">Toggle menu</span>
									</Button>
								</div>
							</div>
						</div>
					</div>

					{/* Mobile Navigation */}
					{mobileMenuOpen && (
						<div
							className="fixed right-0 left-0 z-40 flex flex-col overflow-y-auto overscroll-contain border-t border-stone-200 bg-white max-[1299px]:flex min-[1300px]:hidden"
							style={{
								top: `${navbarHeight}px`,
								height: `calc(100dvh - ${navbarHeight}px)`,
								width: '100%',
							}}
						>
							<div className="flex-1 divide-y overflow-y-auto px-2 pt-2 pb-3">
								{currentNavItems.map((item) => (
									<MobileNavItem
										key={item.name}
										item={item}
										openDropdown={mobileDropdown}
										setOpenDropdown={setMobileDropdown}
									/>
								))}
								<div className="ml-auto hidden items-center space-x-4 min-[1300px]:flex">
									{!isLoggedIn && !viewerName ? (
										<>
											<LoginButton isLandlordMode={isLandlordMode} />
											<SignupButton isLandlordMode={isLandlordMode} />
										</>
									) : (
										<UserDropdown
											submit={submit}
											user={user}
											blurHeader={blurHeader}
											viewerName={viewerName}
										/>
									)}
								</div>
							</div>

							{!isLoggedIn && (
								<div className="px-4 py-4 shadow-[0px_1px_15.6px_0px_#8A9FB340]">
									<div className="flex items-center justify-start gap-3">
										<Link
											to="/blog"
											className="text-sm font-medium text-[#32302F] transition-colors hover:text-black"
										>
											Blog
										</Link>
										<LoginButton isLandlordMode={isLandlordMode} />
										<SignupButton isLandlordMode={isLandlordMode} />
									</div>
								</div>
							)}

							{isLoggedIn && (
								<div className="border-t border-stone-200 px-4 py-4">
									<MobileUserMenu submit={submit} />
								</div>
							)}
						</div>
					)}

					{/* Desktop Dropdown Overlay - Fixed positioning to prevent navbar scroll */}
					{desktopDropdown && (
						<div
							className="fixed right-0 left-0 z-[60] border-t border-stone-200 bg-white shadow-[0px_4px_4px_0px_#00000014]"
							style={{
								top: `${navbarHeight}px`,
							}}
							onMouseLeave={() => setDesktopDropdown(null)}
						>
							{currentNavItems
								.filter((item) => item.children && item.children.length > 0)
								.map((item) => {
									if (desktopDropdown !== item.name) return null
									return (
										<DropdownPanel
											key={item.name}
											item={item}
											onClose={() => setDesktopDropdown(null)}
										/>
									)
								})}
						</div>
					)}
				</div>
			</div>
		</>
	)
}

function NavItem({ item, openDropdown, setOpenDropdown }: NavItemProps) {
	const hasChildren = item.children && item.children.length > 0
	const location = useLocation()
	// Check if current route is any landlord-related route
	const landlordRoutes = content.shared.navigation.landlordRoute
	const isLandlordMode = landlordRoutes.some((route) =>
		location.pathname.startsWith(route),
	)

	// Check if this nav item is active
	const isActive =
		location.pathname === item.href ||
		item.children?.some((child) => {
			if (!child.href) return false
			return (
				location.pathname === child.href ||
				location.pathname.startsWith(`${child.href}/`)
			)
		})

	// Determine if this item should have primary color
	// const shouldBePrimary =
	// 	(!isLandlordMode && item.name === 'Landlord') ||
	// 	(isLandlordMode && item.name === 'Tenant') ||
	// 	isActive

	if (!hasChildren) {
		if (!item.href) return null
		return (
			<Link
				to={item.href}
				className={cn(
					'text-lg font-normal transition-colors',
					item.muted
						? 'cursor-pointer text-black/20'
						: isActive
							? 'font-medium text-[#32302F]'
							: 'font-normal text-[#32302F] hover:font-medium hover:text-black',
				)}
			>
				{item.name}
			</Link>
		)
	}

	const isOpen = openDropdown === item.name

	return (
		<div className="relative">
			<button
				type="button"
				aria-expanded={isOpen}
				onClick={() => setOpenDropdown(isOpen ? null : item.name)}
				onMouseEnter={() => setOpenDropdown(item.name)}
				className={cn(
					'flex h-auto cursor-pointer items-center p-0 text-lg font-normal transition-colors',
					isActive
						? 'font-medium text-[#32302F]'
						: 'font-normal text-[#32302F] hover:font-medium hover:text-black',
				)}
			>
				{item.name}
				<Icon
					name="chevron-down"
					className={cn(
						'ml-1 h-4 w-4 transition-transform duration-200',
						isOpen && 'rotate-180',
					)}
				/>
			</button>
		</div>
	)
}

function DropdownPanel({
	item,
	onClose,
}: {
	item: NavItemType
	onClose: () => void
}) {
	const location = useLocation()

	return (
		<div
			className="w-full border-t border-stone-200 bg-white shadow-[0px_4px_4px_0px_#00000014]"
			onMouseLeave={onClose}
		>
			<div className="mx-auto max-w-full px-4 py-8 min-[1300px]:px-[100px] sm:px-6">
				<div className="grid grid-cols-1 gap-6">
					<div>
						<div className="mb-4 text-xl text-[16px] font-normal text-[#32302F]">
							{item.name === 'How It Works' ? `${item.name}?` : item.name}
						</div>
						<hr className="mb-4 border-stone-300" />
						<div className="flex flex-col gap-3 sm:gap-4">
							{item.children?.map((child: NavItemType) => {
								if (!child.href) return null
								const isChildActive =
									location.pathname === child.href ||
									location.pathname.startsWith(`${child.href}/`)
								return (
									<Link
										key={child.name}
										to={child.href}
										className="transition-colors"
										onClick={onClose}
									>
										<div
											className={cn(
												'text-[16px] font-light transition-colors',
												isChildActive
													? 'text-primary font-medium'
													: 'hover:text-primary text-[#32302F]',
											)}
										>
											{child.name}
										</div>
									</Link>
								)
							})}
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}

function MobileNavItem({ item, openDropdown, setOpenDropdown }: NavItemProps) {
	const hasChildren = item.children && item.children.length > 0
	const location = useLocation()
	// Check if current route is any landlord-related route
	const landlordRoutes = content.shared.navigation.landlordRoute
	const isLandlordMode = landlordRoutes.some((route) =>
		location.pathname.startsWith(route),
	)

	// Check if this nav item is active
	const isActive =
		location.pathname === item.href ||
		item.children?.some((child) => {
			if (!child.href) return false
			return (
				location.pathname === child.href ||
				location.pathname.startsWith(`${child.href}/`)
			)
		})

	// Determine if this item should have primary color
	// const shouldBePrimary =
	// 	(!isLandlordMode && item.name === 'Landlord') ||
	// 	(isLandlordMode && item.name === 'Tenant') ||
	// 	isActive

	if (!hasChildren) {
		if (!item.href) return null
		return (
			<Link
				to={item.href}
				className={cn(
					'block px-3 py-2 text-base transition-colors',
					isActive
						? 'font-medium text-[#32302F]'
						: 'font-normal text-[#32302F] hover:font-medium hover:text-black',
				)}
			>
				{item.name}
			</Link>
		)
	}

	const isOpen = openDropdown === item.name

	return (
		<div>
			<Button
				variant="ghost"
				onClick={() => setOpenDropdown(isOpen ? null : item.name)}
				className={cn(
					'w-full justify-between text-base font-medium transition-colors',
					isActive
						? 'font-medium text-[#32302F]'
						: 'font-normal text-[#32302F] hover:font-medium hover:text-black',
				)}
			>
				{item.name}
				<Icon
					name={isOpen ? 'chevron-up' : 'chevron-down'}
					className="h-4 w-4 transition-transform"
				/>
			</Button>

			{isOpen && item.children && (
				<div className="animate-in slide-in-from-top-2 space-y-1 pl-4 duration-200">
					{item.children.map((child: NavItemType) => {
						if (!child.href) return null
						const isChildActive =
							location.pathname === child.href ||
							location.pathname.startsWith(`${child.href}/`)
						return (
							<Link
								key={child.name}
								to={child.href}
								className={cn(
									'block rounded-md px-3 py-2 text-sm',
									isChildActive
										? 'font-medium text-[#32302F]'
										: 'font-normal text-[#32302F] hover:font-medium hover:text-black',
								)}
							>
								{child.name}
							</Link>
						)
					})}
				</div>
			)}
		</div>
	)
}

function UserDropdown({
	submit,
	user,
	blurHeader = false,
	viewerName,
}: {
	submit: ReturnType<typeof useSubmit>
	user: ReturnType<typeof useOptionalUser>
	blurHeader?: boolean
	viewerName?: string
}) {
	const [open, setOpen] = React.useState(false)
	const dropdownRef = React.useRef<HTMLDivElement>(null)

	const displayName =
		viewerName ||
		[user?.profile?.firstName, user?.profile?.lastName]
			.filter(Boolean)
			.join(' ')
			.trim() ||
		user?.name ||
		''

	// Close dropdown when clicking outside
	React.useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				dropdownRef.current &&
				!dropdownRef.current.contains(event.target as Node)
			) {
				setOpen(false)
			}
		}
		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	return (
		<div className="relative inline-block" ref={dropdownRef}>
			<button
				type="button"
				onClick={() => setOpen((prev) => !prev)}
				className="flex items-center gap-2"
			>
				<Avatar className="h-8 w-8">
					<AvatarImage alt={displayName} src={user?.avatarUrl} />
					<AvatarFallback
						className={cn(
							'bg-[#d9e0ea] text-sm font-medium text-[#32302F]',
							blurHeader && 'bg-[#8e8d8c]',
						)}
					>
						{createAvatarName(displayName)}
					</AvatarFallback>
				</Avatar>
				<span className="hidden text-sm font-medium whitespace-nowrap text-[#32302F] sm:inline">
					{displayName.split(' ').slice(0, 2).join(' ')}
				</span>
				<ChevronDown
					className={cn(
						'h-4 w-4 cursor-pointer transition-transform duration-200',
						open && 'rotate-180',
					)}
				/>
			</button>

			{open && (
				<div className="absolute right-0 z-50 mt-2 w-48 rounded-md border border-stone-200 bg-white shadow-lg">
					<Link
						to="/dashboard"
						className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-100"
						onClick={() => setOpen(false)}
					>
						Dashboard
					</Link>
					{/* <Link
						to="/settings"
						className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-100"
						onClick={() => setOpen(false)}
					>
						Settings
					</Link>
					<Link
						to="/contact"
						className="block px-4 py-2 text-sm text-stone-700 hover:bg-stone-100"
						onClick={() => setOpen(false)}
					>
						Contact Us
					</Link>
					<hr className="border-stone-200 my-1" /> */}
					<button
						type="button"
						className="w-full px-4 py-2 text-left text-sm text-stone-700 hover:bg-stone-100"
						onClick={() => {
							const formData = new FormData()
							void submit(formData, { action: '/logout', method: 'POST' })
							setOpen(false)
						}}
					>
						Logout
					</button>
				</div>
			)}
		</div>
	)
}

function MobileUserMenu({ submit }: { submit: ReturnType<typeof useSubmit> }) {
	return (
		<div className="space-y-1">
			<Link
				to="/dashboard"
				className="flex rounded-md px-3 py-2 text-base text-slate-700 hover:bg-slate-50 hover:text-slate-900"
			>
				<LayoutDashboard className="mr-2" width={18} />
				Dashboard
			</Link>
			<Link
				to="/settings"
				className="flex rounded-md px-3 py-2 text-base text-slate-700 hover:bg-slate-50 hover:text-slate-900"
			>
				<Settings className="mr-2" width={18} />
				Settings
			</Link>
			{/* <Link
				to="/contact"
				className="block rounded-md px-3 py-2 text-base text-slate-700 hover:bg-slate-50 hover:text-slate-900"
			>
				Contact Us
			</Link> */}
			<button
				type="button"
				onClick={() => {
					const formData = new FormData()
					void submit(formData, {
						action: '/logout',
						method: 'POST',
					})
				}}
				className="flex w-full rounded-md px-3 py-2 text-left text-base text-slate-700 hover:bg-slate-50 hover:text-slate-900"
			>
				<Power className="mr-2" width={18} />
				Logout
			</button>
		</div>
	)
}

function ToggleButtons({ isLandlordMode }: { isLandlordMode: boolean }) {
	const [tenantHovered, setTenantHovered] = useState(false)
	const [landlordHovered, setLandlordHovered] = useState(false)
	const shouldAnimate = !prefersReducedMotion()

	const [hideToggle, setHideToggle] = React.useState(false)

	React.useEffect(() => {
		const check = () => {
			setHideToggle(document.body.hasAttribute('data-has-screening-progress'))
		}

		check()

		const observer = new MutationObserver(check)
		observer.observe(document.body, {
			attributes: true,
			attributeFilter: ['data-has-screening-progress'],
		})

		return () => observer.disconnect()
	}, [])

	// React Spring animations for Tenant button - bouncy transitions
	const tenantSpring = useSpring({
		backgroundColor: !isLandlordMode ? '#32302F' : 'transparent',
		color: !isLandlordMode ? '#FFFFFF' : '#475569',
		scale: tenantHovered && !isLandlordMode ? 1.05 : 1,
		config: bouncySpring,
		immediate: !shouldAnimate,
	})

	// React Spring animations for Landlord button - bouncy transitions
	const landlordSpring = useSpring({
		backgroundColor: isLandlordMode ? '#32302F' : 'transparent',
		color: isLandlordMode ? '#FFFFFF' : '#475569',
		scale: landlordHovered && isLandlordMode ? 1.05 : 1,
		config: bouncySpring,
		immediate: !shouldAnimate,
	})

	return (
		<div className="relative flex items-center gap-0 rounded-br-[12px] rounded-bl-[12px] bg-white">
			<animated.div
				onMouseEnter={() => setTenantHovered(true)}
				onMouseLeave={() => setTenantHovered(false)}
				style={{
					transform: tenantSpring.scale.to((s) => `scale(${s})`),
					backgroundColor: tenantSpring.backgroundColor,
				}}
				className="overflow-hidden rounded-br-[12px] rounded-bl-[12px]"
			>
				<animated.div
					style={{
						color: tenantSpring.color,
					}}
					className="px-5 py-[8px] text-xs"
				>
					<Link to="/" className="block">
						{content.shared.buttons.tenant}
					</Link>
				</animated.div>
			</animated.div>
			<animated.div
				onMouseEnter={() => setLandlordHovered(true)}
				onMouseLeave={() => setLandlordHovered(false)}
				style={{
					transform: landlordSpring.scale.to((s) => `scale(${s})`),
					backgroundColor: landlordSpring.backgroundColor,
				}}
				className="overflow-hidden rounded-br-[12px] rounded-bl-[12px]"
			>
				<animated.div
					style={{
						color: landlordSpring.color,
					}}
					className="px-5 py-[8px] text-xs"
				>
					<Link to="/landlord" className="block">
						{content.shared.buttons.landlord}
					</Link>
				</animated.div>
			</animated.div>
		</div>
	)
}

function LogoButton({ isLoggedIn: _isLoggedIn }: { isLoggedIn: boolean }) {
	return (
		<Link to={'/'}>
			<div className="flex items-center">
				<div className="w-32 min-[1300px]:w-[250px] sm:w-40">
					<Logo />
				</div>
			</div>
		</Link>
	)
}

function LoginButton({ isLandlordMode }: { isLandlordMode: boolean }) {
	const loginPath = isLandlordMode ? '/landlord/login' : '/tenant/login'
	return (
		<Link
			to={loginPath}
			className="inline-flex items-center justify-center rounded-[52px] px-6 py-2 text-sm font-medium text-[#32302F] transition-all hover:border-slate-400 hover:bg-slate-100 active:scale-[0.98]"
		>
			{content.shared.buttons.login}
		</Link>
	)
}

function SignupButton({ isLandlordMode }: { isLandlordMode: boolean }) {
	const signupPath = isLandlordMode ? '/landlord/signup' : '/tenant/signup'
	return (
		<AnimatedButton
			asChild
			className="rounded-[52px] bg-[#32302F] text-[#FFFFFF] hover:bg-[#1a1918]"
		>
			<Link to={signupPath}>{content.shared.buttons.getStarted}</Link>
		</AnimatedButton>
	)
}
